<template>
  <div>
    <LeftSidebar
      :visible="visible"
      baseClass="edit-user-wrapper"
      :title="title"
      :hasHeaderRightIcon="false"
    >
      <template>
        <FormLoading
          :loading="loading"
          :count="8"
          :class="{ 'padding-top-16': loading }"
        >
          <template>
            <div class="padding-16">
              <Input
                :title="$lang.username"
                :label="$lang.username"
                :placeholder="$lang.username"
                :value="form.username"
                name="username"
                id="username"
                type="text"
                :vuelidateObj="$v.form.username"
                :disabled="true"
                rowClass="margin-bottom-8"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
              />
              <Button
                v-if="canChangeUserPass"
                variant="outline-primary"
                :text="$lang.changeUsernameAndPassword"
                class="width-percent-100 margin-bottom-24"
                contentClass="justify-content-center"
                :loading="loading"
                @click="handleOpenUserPassModal"
              />
              <Input
                :title="$lang.mobile"
                :label="$lang.mobile"
                :placeholder="$lang.mobile"
                :value="form.mobile"
                name="mobile"
                id="mobile"
                type="text"
                :minLength="lengthNumber11"
                :maxLength="lengthNumber11"
                :vuelidateObj="$v.form.mobile"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
              />
              <Input
                :title="`${$lang.email} (${$lang.optional})`"
                :label="$lang.email"
                :placeholder="$lang.email"
                :value="form.email"
                name="email"
                id="email"
                type="email"
                :vuelidateObj="$v.form.email"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
              />
              <DatePicker
                :title="$lang.expire_at"
                :label="$lang.expire_at"
                :placeholder="$lang.expire_at"
                :value="form.expireAt"
                name="expireAt"
                id="expireAt"
                rightIcon="calendar-blank"
                displayFormat="jYYYY/jMM/jDD"
                format="YYYY/MM/DD"
                type="date"
                :popover="true"
                :simple="true"
                :autoSubmit="true"
                :hasEraseIcon="true"
                :vuelidateObj="$v.form.expireAt"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
                @erase="eraseDatePicker"
              />
              <Checkbox
                v-if="isActiveSmsPanel()"
                :label="$lang.two_step_verification"
                :value="form.twoStep"
                name="twoStep"
                @input="input"
              />
              <Checkbox
                v-if="isActiveSmsPanel()"
                :label="$lang.login_notify"
                :value="form.loginNotify"
                name="loginNotify"
                @input="input"
              />
              <Button
                variant="outline-primary"
                :text="form.isEnable ? $lang.disableUser : $lang.enableUser"
                class="width-percent-100 margin-bottom-24"
                contentClass="justify-content-center"
                :loading="loading"
                @click="handleOpenIsEnableModal"
              />
            </div>
          </template>
        </FormLoading>
      </template>
      <template #footer>
        <Button
          variant="filled-complementary"
          :text="$lang.cancel"
          class="mobile-width-percent-100 tablet-width-percent-100"
          contentClass="justify-content-center"
          @click="close"
        />
        <Button
          v-if="checkPermission(permissions, ['admin', 'edit']) && !loading"
          variant="primary"
          :text="$lang.edit"
          class="mobile-width-percent-100 tablet-width-percent-100"
          contentClass="justify-content-center"
          :loading="loading"
          @click="submit"
        />
      </template>
    </LeftSidebar>
    <Modal
      :show="isEnableModal"
      :title="form.isEnable ? $lang.disableUser : $lang.enableUser"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="true"
      :okButton="true"
      :okButtonText="$lang.yes"
      size="sm"
      :ok="changeIsEnable"
      @close="handleCloseIsEnableModal"
    >
      <template #body>
        <div
          class="color-complementary-300 line-height-24"
          v-html="generateIsEnableMessage"
        ></div>
      </template>
    </Modal>
    <Modal
      :show="isUserPassModal"
      :title="$lang.changeUsernameAndPassword"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="false"
      :okButton="true"
      :okButtonText="$lang.submit"
      size="sm"
    >
      <template #body>
        <UserPassForm
          :code="code"
          :currentUsername="form.username"
          @getUserDetail="getUserDetail"
          @close="handleCloseUserPassModal"
        />
      </template>
    </Modal>
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import LeftSidebar from "@/components/re-design/sidebar/LeftSidebar.vue";
import {
  Input,
  DatePicker,
  Checkbox,
  Button,
  FormLoading,
} from "@/components/re-design/forms";
import Modal from "@/components/re-design/modal/Modal.vue";
import UserPassForm from "./UserPassForm.vue";
import { minLength, maxLength, numeric } from "vuelidate/lib/validators";
import { pathOr, includes } from "ramda";
import {
  getUser,
  editUser,
  changeUserIsEnable,
} from "@/modules/profile/services";
import {
  checkPersianCharValidation,
  isActiveSmsPanel,
  checkPermission,
  generateError,
  toastAlert,
} from "@/shared/utils";

export default {
  mixins: [inputMixin],
  components: {
    LeftSidebar,
    Input,
    DatePicker,
    Checkbox,
    Button,
    FormLoading,
    Modal,
    UserPassForm,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    code: {
      type: String,
      default: null,
    },
    canChangeUserPass: {
      type: Boolean,
      default: false,
    },
  },
  validations() {
    return {
      form: {
        username: {},
        mobile: {
          numeric,
          persianCharacters: (val) => !checkPersianCharValidation(val),
          minLength: minLength(this.lengthNumber11),
          maxLength: maxLength(this.lengthNumber11),
        },
        email: {
          persianCharacters: (val) => !checkPersianCharValidation(val),
        },
        expireAt: {},
      },
    };
  },
  data() {
    return {
      form: {
        username: null,
        mobile: null,
        email: null,
        expireAt: null,
        twoStep: false,
        loginNotify: false,
        isEnable: false,
      },
      loading: false,
      title: "",
      permissions: [],
      isEnableModal: false,
      isUserPassModal: false,
    };
  },
  computed: {
    generateIsEnableMessage() {
      const text = this.form.isEnable
        ? this.$lang.disableUserMessage
        : this.$lang.enableUserMessage;
      return `${text} ${this.$lang.areYouSure}`;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.resetForm(); // read this method of input mixin for reset all input values of form object
          this.getUserDetail();
        }
      },
    },
  },
  methods: {
    pathOr,
    includes,
    checkPersianCharValidation,
    isActiveSmsPanel,
    checkPermission,
    close() {
      this.$emit("close");
      this.resetForm();
      this.permissions = [];
      this.title = "";
    },
    eraseDatePicker(key) {
      this.input(key, null);
    },
    async getUserDetail() {
      try {
        this.loading = true;
        const response = await getUser(this.code);
        const {
          full_name,
          username,
          mobile,
          email,
          twoStep,
          loginNotify,
          isEnable,
          permissions,
        } = response;
        this.title = `${this.$lang.user_info} ${full_name}`;
        this.form.username = username;
        this.form.mobile = mobile;
        this.form.email = email;
        this.form.twoStep = twoStep;
        this.form.loginNotify = loginNotify;
        this.form.isEnable = isEnable;
        this.permissions = permissions;
      } catch (error) {
        generateError(error);
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      try {
        this.loading = true;
        const { mobile, email, expireAt, twoStep, loginNotify } = this.form;
        const response = await editUser(
          this.code,
          mobile,
          email,
          expireAt,
          twoStep,
          loginNotify
        );
        const { message } = response;
        toastAlert(message);
      } catch (error) {
        generateError(error);
      } finally {
        this.loading = false;
      }
    },
    handleOpenIsEnableModal() {
      this.isEnableModal = true;
    },
    handleCloseIsEnableModal() {
      this.isEnableModal = false;
    },
    async changeIsEnable() {
      try {
        const isEnable = Number(!this.form.isEnable);
        const response = await changeUserIsEnable(this.code, isEnable);
        const { message } = response;
        toastAlert(message);
        this.handleCloseIsEnableModal();
        this.getUserDetail();
      } catch (error) {
        generateError(error);
      }
    },
    handleOpenUserPassModal() {
      this.isUserPassModal = true;
    },
    handleCloseUserPassModal() {
      this.isUserPassModal = false;
    },
  },
};
</script>
